import { useRef } from 'react';
import { Zap, Package, User, Printer } from 'lucide-react';
import { triggerPrint } from '../lib/printUtils';

const fmt = (n) => {
  if (n === undefined || n === null) return '0';
  return Math.round(Number(n)).toLocaleString('es-CL');
};

const fmtDate = (d) => {
  if (!d) return '-';
  try {
    const parts = d.split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  } catch { }
  return d;
};

const REF_TYPES = {
  '33': 'Factura',
  '34': 'Factura Exenta',
  '52': 'Guía Despacho',
  '56': 'Nota Débito',
  '61': 'Nota Crédito',
  '801': 'Orden de Compra',
  '802': 'Nota Pedido',
  '803': 'Contrato',
  '804': 'Resolución',
  '805': 'Proceso ChileCompra',
  '806': 'Ficha ChileCompra',
  'HES': 'Hoja Entrada Servicio',
  'SET': 'Set de Pruebas',
};

export function FacturaViewer({ dte, operaciones = [], isExpressOC = null }) {
  const printRef = useRef(null);
  if (!dte) return null;
  const emisor = dte.emisor || {};
  const receptor = dte.receptor || {};
  const totales = dte.totales || {};
  const detalle = dte.detalle || [];
  const ocRef = dte.orden_compra || '';
  // Si viene el prop explícito, úsalo; si no, fallback al prefijo W (OCs históricas).
  const isExpress = isExpressOC !== null ? isExpressOC : String(ocRef).startsWith('W');

  // Iter 235.b (Carlos): Fase 2 — campos enriquecidos del parser SII
  const termPago = dte.term_pago || {};
  const periodo = dte.periodo_facturado || {};
  const transporte = dte.transporte || {};
  const cancelado = dte.cancelado_en_docto || {};
  const tieneContactoEmisor = !!(emisor.contacto || emisor.telefono || emisor.correo || emisor.sucursal_sii);
  const tienePeriodo = !!(periodo.desde || periodo.hasta);
  const tieneTransporte = !!(transporte.patente || transporte.nombre_chofer || transporte.dir_dest);
  const tienePagoEnDocto = !!(cancelado.fecha || (cancelado.monto && cancelado.monto > 0));
  const tieneTermPago = !!(termPago.dias || termPago.glosa);

  // Iter 285 (Carlos): imprimir el documento desplegado en tamaño Carta (letter)
  const handlePrint = () => {
    const node = printRef.current;
    if (!node) return;
    const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
      .map((el) => el.outerHTML)
      .join('\n');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<base href="${window.location.origin}/">
<title>DTE Folio ${dte.folio || ''} — ${emisor.razon_social || ''}</title>
${styles}
<style>
  @page { size: letter portrait; margin: 10mm; }
  html, body { margin: 0; padding: 0; background: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .dte-print-root { max-width: 100%; }
</style>
</head><body><div class="dte-print-root">${node.outerHTML}</div></body></html>`;
    triggerPrint(html);
  };

  return (
    <div>
      <div className="flex justify-end mb-2">
        <button
          onClick={handlePrint}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 hover:text-gray-900 transition-colors"
          title="Imprimir este documento (tamaño Carta)"
          data-testid="btn-imprimir-dte"
        >
          <Printer className="w-3.5 h-3.5" /> Imprimir (Carta)
        </button>
      </div>
      <div ref={printRef} className="bg-white border-2 border-gray-800 text-[11px] leading-tight font-sans" data-testid="factura-viewer">
      {/* === HEADER: Emisor + Red Box === */}
      <div className="flex border-b-2 border-gray-800">
        {/* Left: Emisor info */}
        <div className="flex-1 p-3 border-r-2 border-gray-800">
          <p className="text-[13px] font-black uppercase tracking-tight text-gray-900">{emisor.razon_social}</p>
          <p className="text-[10px] text-gray-600 mt-0.5">{emisor.giro}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{emisor.direccion}</p>
          <p className="text-[10px] text-gray-500">{emisor.comuna}{emisor.ciudad && emisor.ciudad !== emisor.comuna ? ` - ${emisor.ciudad}` : ''}</p>
          {/* Iter 235.b (Carlos): contacto emisor (correo, telefono, sucursal) */}
          {tieneContactoEmisor && (
            <div className="mt-1.5 pt-1.5 border-t border-gray-200 text-[9px] text-gray-600 space-y-0.5">
              {emisor.contacto && <p>👤 {emisor.contacto}</p>}
              {emisor.correo && (
                <p>
                  ✉️ <a href={`mailto:${emisor.correo}`} className="text-blue-600 hover:underline">{emisor.correo}</a>
                </p>
              )}
              {emisor.telefono && (
                <p>
                  📞 <a href={`tel:${emisor.telefono.replace(/\s/g, '')}`} className="text-blue-600 hover:underline">{emisor.telefono}</a>
                </p>
              )}
              {emisor.sucursal_sii && (
                <p className="text-gray-500">Sucursal SII: <span className="font-mono">{emisor.sucursal_sii}</span></p>
              )}
            </div>
          )}
        </div>
        {/* Right: Red box */}
        <div className="w-[220px] p-3 flex flex-col items-center justify-center border-2 border-red-600 m-2 rounded">
          <p className="text-[11px] font-bold text-red-700">R.U.T.: {emisor.rut}</p>
          <p className="text-[12px] font-black text-red-700 mt-1 text-center">{dte.tipo_dte_nombre || 'FACTURA ELECTRONICA'}</p>
          <p className="text-[14px] font-black text-red-700 mt-0.5">N° {dte.folio}</p>
        </div>
      </div>

      {/* === RECEPTOR + Fecha Emision === */}
      <div className="border-b-2 border-gray-800 p-3">
        <div className="flex justify-between">
          <div className="flex-1">
            <table className="text-[10px]">
              <tbody>
                <tr>
                  <td className="font-bold pr-2 py-0.5 align-top whitespace-nowrap">SENOR(ES):</td>
                  <td className="py-0.5">{receptor.razon_social}</td>
                </tr>
                <tr>
                  <td className="font-bold pr-2 py-0.5 whitespace-nowrap">R.U.T.:</td>
                  <td className="py-0.5">{receptor.rut}</td>
                </tr>
                <tr>
                  <td className="font-bold pr-2 py-0.5 whitespace-nowrap">GIRO:</td>
                  <td className="py-0.5">{receptor.giro}</td>
                </tr>
                <tr>
                  <td className="font-bold pr-2 py-0.5 whitespace-nowrap">DIRECCION:</td>
                  <td className="py-0.5">{receptor.direccion}</td>
                </tr>
                <tr>
                  <td className="font-bold pr-2 py-0.5 whitespace-nowrap">COMUNA:</td>
                  <td className="py-0.5">{receptor.comuna}</td>
                </tr>
                {receptor.ciudad && (
                  <tr>
                    <td className="font-bold pr-2 py-0.5 whitespace-nowrap">CIUDAD:</td>
                    <td className="py-0.5">{receptor.ciudad}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="text-right pl-4">
            <p className="text-[10px] text-gray-500">Fecha Emision:</p>
            <p className="text-[11px] font-bold">{fmtDate(dte.fecha_emision)}</p>
            {dte.fecha_vencimiento && dte.fecha_vencimiento !== dte.fecha_emision && (
              <>
                <p className="text-[10px] text-gray-500 mt-1">Fecha Vencimiento:</p>
                <p className="text-[11px] font-bold">{fmtDate(dte.fecha_vencimiento)}</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* === DETALLE ITEMS === */}
      <div className="border-b-2 border-gray-800">
        {detalle.length === 0 ? (
          <div className="px-4 py-3 bg-amber-50 border-l-4 border-amber-400" data-testid="dte-sin-detalle">
            <p className="text-[11px] font-semibold text-amber-900">Sin detalle de líneas</p>
            <p className="text-[10px] text-amber-800 mt-1">
              {dte.origen === 'rcv_provisional' || dte.fuente === 'sii_rcv'
                ? 'Esta es una pre-factura desde el RCV (Registro de Compras del SII). Solo trae totales agregados; los ítems detallados llegarán cuando arribe el XML real al buzón dte@.'
                : 'El XML de este DTE no incluyó líneas de detalle. Solo se contabilizó el total.'}
            </p>
          </div>
        ) : (
        <table className="w-full">
          <thead>
            <tr className="bg-gray-100 border-b border-gray-400">
              <th className="px-2 py-1 text-left text-[9px] font-bold border-r border-gray-300 w-[50px]">Codigo</th>
              <th className="px-2 py-1 text-left text-[9px] font-bold border-r border-gray-300">Descripcion</th>
              <th className="px-2 py-1 text-right text-[9px] font-bold border-r border-gray-300 w-[60px]">Cantidad</th>
              <th className="px-2 py-1 text-left text-[9px] font-bold border-r border-gray-300 w-[40px]">Unid.</th>
              <th className="px-2 py-1 text-right text-[9px] font-bold border-r border-gray-300 w-[70px]">Precio</th>
              <th className="px-2 py-1 text-right text-[9px] font-bold border-r border-gray-300 w-[55px]">Desc.</th>
              <th className="px-2 py-1 text-right text-[9px] font-bold w-[80px]">Valor</th>
            </tr>
          </thead>
          <tbody>
            {detalle.map((d, i) => (
              <tr key={i} className="border-b border-gray-200">
                <td className="px-2 py-1 border-r border-gray-200 text-[10px] text-gray-500 font-mono">{d.codigo || ''}</td>
                <td className="px-2 py-1 border-r border-gray-200">
                  <p className="text-[10px] font-medium text-gray-900">{d.nombre || d.nombre_item}</p>
                  {d.descripcion && d.descripcion !== d.nombre && d.descripcion !== d.nombre_item && (
                    <p className="text-[9px] text-gray-500 italic">{d.descripcion}</p>
                  )}
                  {d.numero_linea && (
                    <span className="text-[8px] text-gray-400">L{d.numero_linea}</span>
                  )}
                </td>
                <td className="px-2 py-1 text-right border-r border-gray-200 text-[10px]">
                  {d.cantidad != null ? Number(d.cantidad).toLocaleString('es-CL', {maximumFractionDigits: 2}) : ''}
                </td>
                <td className="px-2 py-1 border-r border-gray-200 text-[9px] text-gray-600">{d.unidad || '-'}</td>
                <td className="px-2 py-1 text-right border-r border-gray-200 text-[10px]">
                  {d.precio_unitario != null ? Number(d.precio_unitario).toLocaleString('es-CL', {maximumFractionDigits: 2}) : ''}
                </td>
                <td className="px-2 py-1 text-right border-r border-gray-200 text-[9px] text-red-600">
                  {d.descuento ? `-${Number(d.descuento).toLocaleString('es-CL')}` : ''}
                </td>
                <td className="px-2 py-1 text-right text-[10px] font-medium">{fmt(d.monto_item)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>

      {/* === REFERENCIAS === */}
      {(ocRef || (dte.referencias || []).length > 0) && (
        <div className="border-b-2 border-gray-800 px-3 py-2">
          <p className="text-[10px] font-black uppercase tracking-wider text-gray-700 mb-1">REFERENCIAS</p>
          <div className="space-y-1">
            {ocRef && (
              <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[10px] font-semibold ${
                isExpress ? 'bg-amber-50 text-amber-800 border border-amber-300' : 'bg-green-50 text-green-800 border border-green-300'
              }`}>
                {isExpress ? <Zap className="w-3 h-3" /> : <Package className="w-3 h-3" />}
                {isExpress ? 'OC Express' : 'Orden de Compra'}: {ocRef}
              </div>
            )}
            {(dte.referencias || []).filter(r => r.tipo_doc !== '801' || !ocRef).map((ref, i) => (
              <div key={i} className="flex items-center gap-2 text-[9px] text-gray-600">
                <span className="font-semibold">{REF_TYPES[ref.tipo_doc] || `Doc. ${ref.tipo_doc}`}:</span>
                <span>{ref.folio_ref}</span>
                {ref.fecha_ref && <span className="text-gray-400">({ref.fecha_ref})</span>}
                {ref.razon_ref && <span className="italic">{ref.razon_ref}</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === Iter 235.b (Carlos): Info SII oficial enriquecida — Período / TermPago / Transporte / Pago en docto === */}
      {(tieneTermPago || tienePeriodo || tieneTransporte || tienePagoEnDocto) && (
        <div className="border-b-2 border-gray-800 px-3 py-2 bg-slate-50">
          <p className="text-[10px] font-black uppercase tracking-wider text-gray-700 mb-1.5">INFO SII</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-[10px]">
            {/* TermPago oficial SII */}
            {tieneTermPago && (
              <div className="bg-white border border-purple-200 rounded p-1.5" data-testid="fv-termpago">
                <p className="text-[8px] uppercase font-bold text-purple-700 mb-0.5">TermPago SII</p>
                {termPago.dias > 0 && (
                  <p className="font-semibold text-gray-900">{termPago.dias} días</p>
                )}
                {termPago.glosa && (
                  <p className="text-gray-600 italic text-[9px]">{termPago.glosa}</p>
                )}
                {dte.fma_pag_glosa && (
                  <p className="text-gray-500 mt-0.5">FmaPag: {dte.fma_pag_glosa}</p>
                )}
              </div>
            )}
            {/* Período facturado */}
            {tienePeriodo && (
              <div className="bg-white border border-cyan-200 rounded p-1.5" data-testid="fv-periodo">
                <p className="text-[8px] uppercase font-bold text-cyan-700 mb-0.5">Período facturado</p>
                <p><span className="text-gray-500">Desde:</span> <span className="font-medium">{periodo.desde}</span></p>
                <p><span className="text-gray-500">Hasta:</span> <span className="font-medium">{periodo.hasta}</span></p>
              </div>
            )}
            {/* Transporte */}
            {tieneTransporte && (
              <div className="bg-white border border-orange-200 rounded p-1.5" data-testid="fv-transporte">
                <p className="text-[8px] uppercase font-bold text-orange-700 mb-0.5">Transporte</p>
                {transporte.patente && (
                  <p><span className="text-gray-500">Pat:</span> <span className="font-mono font-bold">{transporte.patente}</span></p>
                )}
                {transporte.nombre_chofer && (
                  <p className="truncate" title={transporte.nombre_chofer}>👤 {transporte.nombre_chofer}</p>
                )}
                {transporte.dir_dest && (
                  <p className="truncate text-gray-600 text-[9px]" title={transporte.dir_dest}>📍 {transporte.dir_dest}</p>
                )}
                {transporte.comuna_dest && (
                  <p className="text-gray-500 text-[9px]">{transporte.comuna_dest}</p>
                )}
              </div>
            )}
            {/* Pago en docto */}
            {tienePagoEnDocto && (
              <div className="bg-white border border-emerald-200 rounded p-1.5" data-testid="fv-pago-docto">
                <p className="text-[8px] uppercase font-bold text-emerald-700 mb-0.5">Pago en docto</p>
                {cancelado.fecha && <p><span className="text-gray-500">F:</span> <span className="font-medium">{cancelado.fecha}</span></p>}
                {cancelado.monto > 0 && (
                  <p><span className="text-gray-500">$:</span> <span className="text-emerald-700 font-semibold">${fmt(cancelado.monto)}</span></p>
                )}
                <p>
                  <span className="text-gray-500">Saldo:</span>{' '}
                  <span className={`font-semibold ${cancelado.saldo_insoluto > 0 ? 'text-red-700' : 'text-gray-700'}`}>
                    ${fmt(cancelado.saldo_insoluto)}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* === TOTALES === */}
      <div className="border-b-2 border-gray-800 flex justify-end">
        <table className="text-[10px] m-3">
          <tbody>
            {totales.exento > 0 && (
              <tr>
                <td className="pr-4 py-0.5 text-right font-bold">MONTO EXENTO</td>
                <td className="py-0.5 text-right w-[90px]">$ {fmt(totales.exento)}</td>
              </tr>
            )}
            <tr>
              <td className="pr-4 py-0.5 text-right font-bold">MONTO NETO</td>
              <td className="py-0.5 text-right w-[90px]">$ {fmt(totales.neto)}</td>
            </tr>
            <tr>
              <td className="pr-4 py-0.5 text-right font-bold">I.V.A. 19%</td>
              <td className="py-0.5 text-right w-[90px]">$ {fmt(totales.iva)}</td>
            </tr>
            {/* Impuestos adicionales (combustible, retenciones, etc.) */}
            {(totales.impuestos_adicionales || []).map((imp, i) => (
              <tr key={i}>
                <td className="pr-4 py-0.5 text-right font-bold text-[9px]">
                  {imp.nombre || `Imp. cód. ${imp.tipo}`}
                  {imp.tasa ? ` (${imp.tasa}%)` : ''}
                </td>
                <td className={`py-0.5 text-right w-[90px] ${imp.monto < 0 ? 'text-red-600' : ''}`}>
                  $ {fmt(imp.monto)}
                </td>
              </tr>
            ))}
            {/* If no impuestos_adicionales parsed but amounts don't match, show difference */}
            {!(totales.impuestos_adicionales?.length > 0) && (() => {
              const calculated = (totales.neto || 0) + (totales.iva || 0) + (totales.exento || 0);
              const diff = (totales.total || 0) - calculated;
              if (Math.abs(diff) > 1) {
                return (
                  <tr>
                    <td className="pr-4 py-0.5 text-right font-bold text-[9px]">OTROS IMPUESTOS</td>
                    <td className={`py-0.5 text-right w-[90px] ${diff < 0 ? 'text-red-600' : ''}`}>
                      $ {fmt(diff)}
                    </td>
                  </tr>
                );
              }
              return null;
            })()}
            <tr className="border-t-2 border-gray-800">
              <td className="pr-4 py-1 text-right font-black text-[12px]">TOTAL</td>
              <td className="py-1 text-right font-black text-[12px] w-[90px]">$ {fmt(totales.total)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* === TIMBRE ELECTRONICO SII === */}
      <div className="px-3 py-2 flex items-center gap-3 border-b border-gray-300">
        <div className="w-[80px] h-[40px] bg-gray-200 border border-gray-400 flex items-center justify-center text-[7px] text-gray-500 font-mono">
          Timbre SII
        </div>
        <div className="text-[8px] text-gray-500">
          <p>Timbre Electronico SII</p>
          <p>Verifique documento: www.sii.cl</p>
        </div>
      </div>

      {/* === MEDIO DE PAGO === */}
      {dte.medio_pago && (
        <div className="px-3 py-1.5 border-b border-gray-300 flex items-center gap-3" data-testid="dte-medio-pago">
          <span className="text-[9px] font-bold text-gray-700">FORMA DE PAGO:</span>
          <span className={`text-[10px] font-semibold ${
            (dte.medio_pago || '').toLowerCase().includes('tarjeta') || (dte.medio_pago || '').toUpperCase().startsWith('TC')
              ? 'text-orange-600' : 'text-gray-700'
          }`}>
            {dte.medio_pago}
          </span>
          {((dte.medio_pago || '').toLowerCase().includes('tarjeta') || (dte.medio_pago || '').toUpperCase().startsWith('TC')) && (
            <span className="text-[8px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-medium">Tarjeta</span>
          )}
        </div>
      )}

      {/* === OPERACIONES REALIZADAS === */}
      {operaciones.length > 0 && (
        <div className="px-3 py-2 bg-gray-50">
          <p className="text-[9px] font-bold uppercase text-gray-600 mb-1">Operaciones Realizadas</p>
          <div className="space-y-1">
            {operaciones.map((op, i) => (
              <div key={i} className="flex items-center gap-1.5 text-[9px] text-gray-600">
                <User className="w-3 h-3 text-gray-400 flex-shrink-0" />
                <span className="font-semibold text-gray-700">{op.tipo}{op.numero ? ` ${op.numero}` : ''}</span>
                {op.creado_por && (
                  <span>por <span className="text-blue-700">{op.creado_por}</span></span>
                )}
                {op.fecha && (
                  <span className="text-gray-400">
                    ({typeof op.fecha === 'string' && op.fecha.includes('T') 
                      ? new Date(op.fecha).toLocaleDateString('es-CL') 
                      : op.fecha})
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* === INFORMACIÓN ADICIONAL (metadata, RCV extras, observaciones) === */}
      <div className="px-3 py-2 bg-blue-50/50 border-t border-blue-200" data-testid="dte-info-adicional">
        <p className="text-[9px] font-bold uppercase text-blue-800 mb-1.5">Información Adicional</p>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[9px]">
          {/* Origen del DTE */}
          <div>
            <span className="font-semibold text-gray-600">Origen:</span>{' '}
            <span className={`${dte.origen === 'rcv_provisional' ? 'text-amber-700 font-semibold' : 'text-gray-700'}`}>
              {dte.origen === 'rcv_provisional' ? '📋 RCV Provisional (sin XML)' :
               dte.origen === 'rcv_reconciliado' ? '✅ RCV + XML reconciliado' :
               dte.fuente === 'sii_rcv' ? 'SII RCV' :
               'XML buzón electrónico'}
            </span>
          </div>
          {dte.rcv_periodo && (
            <div>
              <span className="font-semibold text-gray-600">Periodo RCV:</span>{' '}
              <span className="text-gray-700 font-mono">{dte.rcv_periodo}</span>
            </div>
          )}
          {dte.tiene_xml === false && (
            <div className="col-span-2 text-amber-700 italic">
              ⚠️ XML aún no recibido — montos provienen del CSV del SII.
            </div>
          )}

          {/* Datos buzón electrónico */}
          {dte.email_from && (
            <div className="col-span-2">
              <span className="font-semibold text-gray-600">Recibido de:</span>{' '}
              <span className="text-gray-700">{dte.email_from}</span>
            </div>
          )}
          {dte.email_subject && (
            <div className="col-span-2 truncate" title={dte.email_subject}>
              <span className="font-semibold text-gray-600">Asunto:</span>{' '}
              <span className="text-gray-700">{dte.email_subject}</span>
            </div>
          )}
          {dte.email_received_at && (
            <div>
              <span className="font-semibold text-gray-600">Buzón:</span>{' '}
              <span className="text-gray-700">
                {new Date(dte.email_received_at).toLocaleString('es-CL', {dateStyle:'short', timeStyle:'short'})}
              </span>
            </div>
          )}
          {dte.attachment_name && (
            <div className="truncate" title={dte.attachment_name}>
              <span className="font-semibold text-gray-600">Archivo:</span>{' '}
              <span className="text-gray-700 font-mono text-[8px]">{dte.attachment_name}</span>
            </div>
          )}
          {dte.parsed_at && (
            <div>
              <span className="font-semibold text-gray-600">Parseado:</span>{' '}
              <span className="text-gray-700">
                {new Date(dte.parsed_at).toLocaleString('es-CL', {dateStyle:'short', timeStyle:'short'})}
              </span>
            </div>
          )}

          {/* Datos extra del RCV (provisionales) */}
          {dte.rcv_extras && Object.keys(dte.rcv_extras).length > 0 && (
            <>
              {dte.rcv_extras.tipo_compra && (
                <div>
                  <span className="font-semibold text-gray-600">Tipo compra SII:</span>{' '}
                  <span className="text-gray-700">{dte.rcv_extras.tipo_compra}</span>
                </div>
              )}
              {dte.rcv_extras.fecha_recepcion_sii && (
                <div>
                  <span className="font-semibold text-gray-600">Recepción SII:</span>{' '}
                  <span className="text-gray-700">{dte.rcv_extras.fecha_recepcion_sii}</span>
                </div>
              )}
              {dte.rcv_extras.fecha_acuse_sii && (
                <div>
                  <span className="font-semibold text-gray-600">Acuse SII:</span>{' '}
                  <span className="text-gray-700">{dte.rcv_extras.fecha_acuse_sii}</span>
                </div>
              )}
              {dte.rcv_extras.iva_no_recuperable_codigo && (
                <div>
                  <span className="font-semibold text-gray-600">IVA no recup.:</span>{' '}
                  <span className="text-gray-700">{dte.rcv_extras.iva_no_recuperable_codigo}</span>
                </div>
              )}
            </>
          )}

          {/* IVA no recuperable de totales */}
          {(dte.totales || {}).iva_no_recuperable > 0 && (
            <div className="col-span-2 text-orange-700">
              <span className="font-semibold">IVA No Recuperable:</span> ${fmt(dte.totales.iva_no_recuperable)}
            </div>
          )}

          {/* Observaciones / Glosa */}
          {dte.observaciones && (
            <div className="col-span-2 mt-1 pt-1 border-t border-blue-200">
              <span className="font-semibold text-gray-600">Observaciones:</span>{' '}
              <span className="text-gray-700 italic">{dte.observaciones}</span>
            </div>
          )}

          {/* IDs / trazabilidad */}
          {dte.id && (
            <div className="col-span-2 text-[8px] text-gray-400 font-mono mt-1">
              ID interno: {dte.id}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}
